import { getDatabase, ref as dbRef, push } from 'firebase/database';
import { PhotoModel } from '../types/protocols';
import { createPhotoModelFromFile, uploadPhotoToDatabaseAndStorage } from './ImageUploadUtils';
import { recordActivity } from '../api/activityApi';

export type ProjectPhotoType = 'beforePictures' | 'progressPictures' | 'afterPictures';

export interface ProjectPhotoUploadResult {
    success: boolean;
    photoModel?: PhotoModel;
    error?: string;
}

/**
 * Creates a database entry for a project photo and uploads it to Storage
 */
export const uploadProjectPhoto = async (
    buildingKey: string,
    projectKey: string,
    photoType: ProjectPhotoType,
    file: File,
    author: string
): Promise<ProjectPhotoUploadResult> => {
    try {
        const db = getDatabase();

        // Generate unique photo key using Firebase push
        // Database uses flat structure: projects/{projectKey}
        const photosRef = dbRef(db, `projects/${projectKey}/${photoType}`);
        const newPhotoRef = push(photosRef);
        const photoKey = newPhotoRef.key!;

        // Storage retains buildingKey for file organization
        const storagePath = `projects/${buildingKey}/${projectKey}/${photoType}/${photoKey}`;
        // Database uses flat structure
        const databasePath = `projects/${projectKey}/${photoType}/${photoKey}`;

        // Create PhotoModel using common utility
        const photoModel = await createPhotoModelFromFile(photoKey, storagePath, author, file);

        // Upload to database and storage using common utility
        const uploadResult = await uploadPhotoToDatabaseAndStorage(databasePath, storagePath, photoModel, file);

        if (!uploadResult.success) {
            return {
                success: false,
                error: uploadResult.error
            };
        }

        recordActivity('photo.uploaded', { buildingKey, targetKey: projectKey });

        return {
            success: true,
            photoModel
        };
    } catch (error) {
        console.error('Error uploading project photo:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};
